import { bgf } from "building-types/buildingGraphFilter/filterBuilder";
import { docEntityBaseNew } from "building-types/entities/docEntityBaseNew";
import { RoomClass, RoomEnclosure } from "building-types/rooms/roomTypesEnum";
import {
  WallPlaneRoofClippingOperationMode,
  WallPlaneRoofClippingSplitDirection,
} from "building-types/walls2/wallPlane";
import { wallPlaneFilterNew, type DocWallPlaneFilter } from "building-types/walls2/wallPlaneFilters";
import type { WallPlaneOperation } from "building-types/walls2/wallPlaneOperations";
import { wallFaceRuleNew, type WallPlaneRuleEntity } from "building-types/walls2/wallPlaneRules";
import type { Table } from "common/data/table";
import {
  defaultWallPlaneCoreAssemblyId,
  defaultWallPlaneEmptyAssemblyId,
  defaultWallPlaneExteriorFinishAssemblyId,
  defaultWallPlaneInteriorFinishAssemblyId,
} from "doc/defaults/defaultWallPlaneAssembly";

export const legacyWallConditionFilterCreators = {
  HOUSE_AT_HOUSE_ROOM_CLASS: () => {
    const rootFilter = bgf.from("FACE_NODE").all().eq("roomClass", RoomClass.HOUSE).build();
    const oppositeFilter = bgf.from("FACE_NODE").all().eq("roomClass", RoomClass.HOUSE).build();
    return {
      ...wallPlaneFilterNew(),
      name: "House @ House Room Class Filter",
      filterGraphId: rootFilter.filterGraphId,
      filterGraphs: {
        ...rootFilter.filterGraphs,
        ...oppositeFilter.filterGraphs,
      },
      subFilter: {
        kind: "OPPOSITE" as const,
        oppositeFilterGraphId: oppositeFilter.filterGraphId,
      },
    };
  },
  HOUSE_AT_PERIMETER_ROOM_CLASS: () => {
    const rootFilter = bgf.from("FACE_NODE").all().eq("roomClass", RoomClass.HOUSE).build();
    return {
      ...wallPlaneFilterNew(),
      name: "House @ Perimeter Room Class Filter",
      filterGraphId: rootFilter.filterGraphId,
      filterGraphs: {
        ...rootFilter.filterGraphs,
      },
      subFilter: {
        kind: "PERIMETER" as const,
      },
    };
  },
  HOUSE_AT_GARAGE_ROOM_CLASS: () => {
    const rootFilter = bgf.from("FACE_NODE").all().eq("roomClass", RoomClass.HOUSE).build();
    const oppositeFilter = bgf.from("FACE_NODE").all().eq("roomClass", RoomClass.GARAGE).build();
    return {
      ...wallPlaneFilterNew(),
      name: "House @ Garage Room Class Filter",
      filterGraphId: rootFilter.filterGraphId,
      filterGraphs: {
        ...rootFilter.filterGraphs,
        ...oppositeFilter.filterGraphs,
      },
      subFilter: {
        kind: "OPPOSITE" as const,
        oppositeFilterGraphId: oppositeFilter.filterGraphId,
      },
    };
  },
  GARAGE_AT_GARAGE_ROOM_CLASS: () => {
    const rootFilter = bgf.from("FACE_NODE").all().eq("roomClass", RoomClass.GARAGE).build();
    const oppositeFilter = bgf.from("FACE_NODE").all().eq("roomClass", RoomClass.GARAGE).build();
    return {
      ...wallPlaneFilterNew(),
      name: "Garage @ Garage Room Class Filter",
      filterGraphId: rootFilter.filterGraphId,
      filterGraphs: {
        ...rootFilter.filterGraphs,
        ...oppositeFilter.filterGraphs,
      },
      subFilter: {
        kind: "OPPOSITE" as const,
        oppositeFilterGraphId: oppositeFilter.filterGraphId,
      },
    };
  },
  GARAGE_AT_PERIMETER_ROOM_CLASS: () => {
    const rootFilter = bgf.from("FACE_NODE").all().eq("roomClass", RoomClass.GARAGE).build();
    return {
      ...wallPlaneFilterNew(),
      name: "Garage @ Perimeter Room Class Filter",
      filterGraphId: rootFilter.filterGraphId,
      filterGraphs: {
        ...rootFilter.filterGraphs,
      },
      subFilter: {
        kind: "PERIMETER" as const,
      },
    };
  },
} satisfies Table<() => DocWallPlaneFilter>;

export const legacyWallConditionFiltersById = Object.fromEntries(
  Object.entries(legacyWallConditionFilterCreators).map(([id, createFilter]) => [
    id,
    {
      ...createFilter(),
      id,
    },
  ]),
) as Table<DocWallPlaneFilter>;

/**
 * @NOTE - these defaults are currently WIP while the correct implementaion of
 * the selector and query system is completed.
 *
 * @TODO WALLS V2: Move built-in wall plane filters into Doc so they are first-class entities.
 */
export const defaultWallPlaneFilters = {
  ALL_WALL_FACE_NODES: {
    ...docEntityBaseNew("WALL_PLANE_FILTER", "ALL_WALL_FACE_NODES"),
    name: "All Wall Faces Filter",
    sortIndex: 0,
    subFilter: { kind: "UNDEFINED" as const },
    ...bgf.from("FACE_NODE").all().build(),
  },
  ALL_ROOM_WALL_FACE_NODES_STRICT: {
    ...docEntityBaseNew("WALL_PLANE_FILTER", "ALL_ROOM_WALL_FACE_NODES_STRICT"),
    name: "All Room Wall Faces Filter",
    sortIndex: 1,
    subFilter: { kind: "UNDEFINED" as const },
    ...bgf
      .from("FACE_NODE")
      .exists("roomHandle")
      .not((s) => s.exists("roofHandle"))
      .build(),
  },
  ALL_ROOF_WALL_FACE_NODES_STRICT: {
    ...docEntityBaseNew("WALL_PLANE_FILTER", "ALL_ROOF_WALL_FACE_NODES_STRICT"),
    name: "All Roof Wall Faces Filter",
    sortIndex: 1,
    subFilter: { kind: "UNDEFINED" as const },
    ...bgf
      .from("FACE_NODE")
      .exists("roofHandle")
      .not((s) => s.exists("roomHandle"))
      .build(),
  },
  ALL_FLOOR_FACE_NODES: {
    ...docEntityBaseNew("WALL_PLANE_FILTER", "ALL_FLOOR_FACE_NODES"),
    name: "All Floor Faces Filter",
    sortIndex: 2,
    subFilter: { kind: "UNDEFINED" as const },
    ...bgf.from("FACE_NODE").exists("floorHandle").build(),
  },
  /**
   * Faces with both room and floor membership on the building perimeter.
   * Used by default exterior floor-plate empty-assembly clipping.
   */
  ROOM_AND_FLOOR_PERIMETER_FACE_NODES: (() => {
    const roomFilter = bgf.from("FACE_NODE").exists("roomHandle");
    const floorFilter = bgf.from("FACE_NODE").exists("floorHandle");
    return {
      ...docEntityBaseNew("WALL_PLANE_FILTER", "ROOM_AND_FLOOR_PERIMETER_FACE_NODES"),
      name: "Room and Floor Perimeter Faces",
      sortIndex: 2,
      subFilter: { kind: "PERIMETER" as const },
      ...bgf.from("FACE_NODE").and(roomFilter, floorFilter).build(),
    };
  })(),
  /**
   * Faces with both room and floor membership whose room enclosure is conditioned,
   * unconditioned, or open, opposite another conditioned/unconditioned/open room face
   * (through-wall). Same OPPOSITE pattern as INTERIOR_ROOM_WALL_FACE_NODES. Open enclosures
   * (e.g. porches) participate here so their room∩floor boundary is clipped to the floor top
   * just like conditioned/unconditioned interior boundaries.
   */
  ROOM_AND_FLOOR_INTERIOR_FACE_NODES: (() => {
    const roomFilter = bgf
      .from("FACE_NODE")
      .exists("roomHandle")
      .in("roomEnclosure", [RoomEnclosure.CONDITIONED, RoomEnclosure.UNCONDITIONED, RoomEnclosure.OPEN]);
    const floorFilter = bgf.from("FACE_NODE").exists("floorHandle");
    const roomAndFloorFilter = bgf.from("FACE_NODE").and(roomFilter, floorFilter).build();
    const oppositeInteriorRoomFilter = bgf
      .from("FACE_NODE")
      .exists("roomHandle")
      .in("roomEnclosure", [RoomEnclosure.CONDITIONED, RoomEnclosure.UNCONDITIONED, RoomEnclosure.OPEN])
      .build();
    return {
      ...docEntityBaseNew("WALL_PLANE_FILTER", "ROOM_AND_FLOOR_INTERIOR_FACE_NODES"),
      name: "Room and Floor Interior Faces",
      sortIndex: 2,
      filterGraphId: roomAndFloorFilter.filterGraphId,
      filterGraphs: {
        ...roomAndFloorFilter.filterGraphs,
        ...oppositeInteriorRoomFilter.filterGraphs,
      },
      subFilter: {
        kind: "OPPOSITE" as const,
        oppositeFilterGraphId: oppositeInteriorRoomFilter.filterGraphId,
      },
    };
  })(),
  /**
   * Face nodes produced by freeform wall path segments. These have no room, floor or
   * roof parent, so the room-gated defaults below never match them.
   *
   * Freeform walls get no default core/finish rules — this filter exists only so users
   * can opt in explicit wall plane rules targeting freeform faces; without one, a
   * freeform wall segment produces no solid mass.
   */
  ALL_FREEFORM_WALL_FACE_NODES: {
    ...docEntityBaseNew("WALL_PLANE_FILTER", "ALL_FREEFORM_WALL_FACE_NODES"),
    name: "All Freeform Wall Faces Filter",
    sortIndex: 2,
    subFilter: { kind: "UNDEFINED" as const },
    ...bgf.from("FACE_NODE").exists("freeformWallHandle").build(),
  },
  /**
   * All face nodes from rooms whose enclosure is conditioned, unconditioned, or
   * exterior. This represents faces which are treated as legacy Solid Walls. We use
   * this filter to generate the default wall core for the house and to apply default exterior finish to the OUTSIDE
   * of All rooms with solid walls
   */
  CONDITIONED_UNCONDITIONED_EXTERIOR_ROOM_ENCLOSURES: {
    ...docEntityBaseNew("WALL_PLANE_FILTER", "CONDITIONED_UNCONDITIONED_EXTERIOR_ROOM_ENCLOSURES"),
    name: "Conditioned/Unconditioned/Exterior Room Enclosures",
    sortIndex: 2,
    subFilter: { kind: "UNDEFINED" as const },
    ...bgf
      .from("FACE_NODE")
      .exists("roomHandle")
      .in("roomEnclosure", [RoomEnclosure.CONDITIONED, RoomEnclosure.UNCONDITIONED, RoomEnclosure.EXTERIOR])
      .build(),
  },
  /**
   * Conditioned/unconditioned room face nodes. Used for applying default interior finish to the INSIDE
   * of rooms with a conditioned/unconditioned enclosure.
   */
  CONDITIONED_UNCONDITIONED_ROOM_ENCLOSURES: {
    ...docEntityBaseNew("WALL_PLANE_FILTER", "CONDITIONED_UNCONDITIONED_ROOM_ENCLOSURES"),
    name: "Conditioned/Unconditioned Room Enclosures",
    sortIndex: 3,
    subFilter: { kind: "UNDEFINED" as const },
    ...bgf
      .from("FACE_NODE")
      .exists("roomHandle")
      .in("roomEnclosure", [RoomEnclosure.CONDITIONED, RoomEnclosure.UNCONDITIONED])
      .build(),
  },
  /**
   * Exterior room face nodes. Used for applying default exterior finish to the INSIDE
   * of rooms with an exterior enclosure.
   */
  EXTERIOR_ROOM_ENCLOSURES: {
    ...docEntityBaseNew("WALL_PLANE_FILTER", "EXTERIOR_ROOM_ENCLOSURES"),
    name: "Exterior Room Enclosures",
    sortIndex: 4,
    subFilter: { kind: "UNDEFINED" as const },
    ...bgf.from("FACE_NODE").exists("roomHandle").eq("roomEnclosure", RoomEnclosure.EXTERIOR).build(),
  },
  INTERIOR_ROOM_WALL_FACE_NODES: (() => {
    const interiorRoomFilter = bgf
      .from("FACE_NODE")
      .exists("roomHandle")
      .in("roomEnclosure", [RoomEnclosure.CONDITIONED, RoomEnclosure.UNCONDITIONED])
      .build();
    const oppositeInteriorRoomFilter = bgf
      .from("FACE_NODE")
      .exists("roomHandle")
      .in("roomEnclosure", [RoomEnclosure.CONDITIONED, RoomEnclosure.UNCONDITIONED])
      .build();
    return {
      ...docEntityBaseNew("WALL_PLANE_FILTER", "INTERIOR_ROOM_WALL_FACE_NODES"),
      name: "Interior Room Wall Faces",
      sortIndex: 5,
      filterGraphId: interiorRoomFilter.filterGraphId,
      filterGraphs: {
        ...interiorRoomFilter.filterGraphs,
        ...oppositeInteriorRoomFilter.filterGraphs,
      },
      subFilter: {
        kind: "OPPOSITE" as const,
        oppositeFilterGraphId: oppositeInteriorRoomFilter.filterGraphId,
      },
    };
  })(),
} satisfies Table<DocWallPlaneFilter>;

type DefaultWallPlaneFilterId = keyof typeof defaultWallPlaneFilters;

export const defaultWallCoreOperation = {
  name: "Default Wall Core",
  operationType: "SET_WALL_ASSEMBLY",
  wallCoreOperator: {
    operationType: "SET_WALL_CORE",
    assemblyId: defaultWallPlaneCoreAssemblyId,
  },
} satisfies WallPlaneOperation;

export const defaultExteriorFinishOperation = {
  name: "Default Exterior Wall Finish",
  operationType: "SET_WALL_ASSEMBLY",
  wallCladdingPositiveOperator: {
    operationType: "SET_WALL_FINISH",
    assemblySide: "POSITIVE_NORMAL",
    assemblyId: defaultWallPlaneExteriorFinishAssemblyId,
  },
} satisfies WallPlaneOperation;

export const defaultInteriorFinishOperation = {
  name: "Default Interior Wall Finish",
  operationType: "SET_WALL_ASSEMBLY",
  wallCladdingPositiveOperator: {
    operationType: "SET_WALL_FINISH",
    assemblySide: "POSITIVE_NORMAL",
    assemblyId: defaultWallPlaneInteriorFinishAssemblyId,
  },
} satisfies WallPlaneOperation;

export const defaultWallCutOperation = {
  name: "Default Wall Cut",
  operationType: "SET_WALL_CUT",
  wallCutOperator: {
    operationType: "SET_WALL_CUT",
  },
} satisfies WallPlaneOperation;

export const defaultRuleCreators = {
  DEFAULT_ROOF_WALLS_RULE: () =>
    wallFaceRuleNew({
      name: "Set Default Roof Wall Assembly",
      sortIndex: 0,
      filterId: "ALL_ROOF_WALL_FACE_NODES_STRICT" satisfies DefaultWallPlaneFilterId,
      direction: "POSITIVE_NORMAL",
      operation: {
        operationType: "SET_WALL_ASSEMBLY",
        name: "Set Default Roof Wall Assembly",
        wallCoreOperator: {
          operationType: "SET_WALL_CORE",
          assemblyId: defaultWallPlaneCoreAssemblyId,
        },
        wallCladdingPositiveOperator: {
          operationType: "SET_WALL_FINISH",
          assemblySide: "POSITIVE_NORMAL",
          assemblyId: defaultWallPlaneExteriorFinishAssemblyId,
        },
      },
    }),
  DEFAULT_ROOF_CLIPPING_RULE: () =>
    wallFaceRuleNew({
      name: "Set Default Roof Clipping",
      sortIndex: 4,
      filterId: "ALL_ROOF_WALL_FACE_NODES_STRICT" satisfies DefaultWallPlaneFilterId,
      direction: "POSITIVE_NORMAL",
      operation: {
        operationType: "SET_WALL_CUT",
        name: "Default Roof Clipping",
        wallCutOperator: {
          operationType: "SET_WALL_CUT",
        },
      },
      operationBoundary: {
        boundaryType: "ROOF_CLIPPING",
        roofSelection: { mode: WallPlaneRoofClippingOperationMode.ALL },
        splitDirection: WallPlaneRoofClippingSplitDirection.ABOVE_ALL,
      },
    }),
  /**
   * Empty core + inward cladding on perimeter room∧floor faces. Outward cladding is left
   * alone so multi-story exterior finish stays one continuous region.
   */
  DEFAULT_FLOOR_CLIPPING_RULE: () => createDefaultFloorPerimeterClippingRule(),
  /**
   * Empty core + both claddings on conditioned/unconditioned room∧floor faces (interior).
   */
  DEFAULT_FLOOR_INTERIOR_CLIPPING_RULE: () => createDefaultFloorInteriorClippingRule(),
  DEFAULT_STAIR_CLIPPING_RULE: () => createDefaultStairClippingRule(),
} satisfies Table<() => WallPlaneRuleEntity>;

function createDefaultFloorPerimeterClippingRule(): WallPlaneRuleEntity {
  return wallFaceRuleNew({
    name: "Set Default Floor Perimeter Clipping",
    sortIndex: 5,
    filterId: "ROOM_AND_FLOOR_PERIMETER_FACE_NODES" satisfies DefaultWallPlaneFilterId,
    direction: "POSITIVE_NORMAL",
    operation: {
      operationType: "SET_WALL_ASSEMBLY",
      name: "Set Default Floor Perimeter Clipping",
      wallCladdingNegativeOperator: {
        operationType: "SET_WALL_FINISH",
        assemblySide: "NEGATIVE_NORMAL",
        assemblyId: defaultWallPlaneEmptyAssemblyId,
      },
      wallCoreOperator: {
        operationType: "SET_WALL_CORE",
        assemblyId: defaultWallPlaneEmptyAssemblyId,
      },
    },
  });
}

function createDefaultFloorInteriorClippingRule(): WallPlaneRuleEntity {
  return wallFaceRuleNew({
    name: "Set Default Floor Interior Clipping",
    sortIndex: 6,
    filterId: "ROOM_AND_FLOOR_INTERIOR_FACE_NODES" satisfies DefaultWallPlaneFilterId,
    direction: "POSITIVE_NORMAL",
    operation: {
      operationType: "SET_WALL_ASSEMBLY",
      name: "Set Default Floor Interior Clipping",
      wallCladdingNegativeOperator: {
        operationType: "SET_WALL_FINISH",
        assemblySide: "NEGATIVE_NORMAL",
        assemblyId: defaultWallPlaneEmptyAssemblyId,
      },
      wallCladdingPositiveOperator: {
        operationType: "SET_WALL_FINISH",
        assemblySide: "POSITIVE_NORMAL",
        assemblyId: defaultWallPlaneEmptyAssemblyId,
      },
      wallCoreOperator: {
        operationType: "SET_WALL_CORE",
        assemblyId: defaultWallPlaneEmptyAssemblyId,
      },
    },
  });
}

function createDefaultStairClippingRule(): WallPlaneRuleEntity {
  return wallFaceRuleNew({
    name: "Set Default Stair Clipping",
    sortIndex: 7,
    filterId: "ALL_ROOM_WALL_FACE_NODES_STRICT" satisfies DefaultWallPlaneFilterId,
    direction: "POSITIVE_NORMAL",
    operation: {
      operationType: "SET_WALL_CUT",
      name: "Default Stair Clipping",
      wallCutOperator: {
        operationType: "SET_WALL_CUT",
      },
    },
    operationBoundary: {
      boundaryType: "STAIR_CLIPPING",
    },
  });
}

export const defaultRuleSetCreators = {
  DEFAULT_ROOF_WALLS: () => {
    const roofWalls = defaultRuleCreators.DEFAULT_ROOF_WALLS_RULE();
    const roofWallClipping = defaultRuleCreators.DEFAULT_ROOF_CLIPPING_RULE();
    return [roofWalls, roofWallClipping];
  },
  DEFAULT_FLOOR_WALLS: () => {
    return [
      defaultRuleCreators.DEFAULT_FLOOR_CLIPPING_RULE(),
      defaultRuleCreators.DEFAULT_FLOOR_INTERIOR_CLIPPING_RULE(),
    ];
  },
} satisfies Table<() => WallPlaneRuleEntity[]>;

export const defaultRuleCreatorsById = Object.fromEntries(
  Object.entries(defaultRuleCreators).map(([id, createRule]) => [
    id,
    {
      ...createRule(),
      id,
    },
  ]),
) as Table<WallPlaneRuleEntity>;

/**
 * @IMPORTANT !!!!!!!
 * This set of rules represents the default walls of the house. The order of these
 * is critical as it determines the order of wall assembly core and cladding operations applied to the walls.
 * This list should be thought of in the same way as a user editing the walls manually in studio
 */
export const defaultWallPlaneRules = {
  /**
   * 1. Fist, we select all the building graph faces that should be treated as solid walls,
   * and apply the default wall core to them.
   */
  DEFAULT_WALL_CORE_RULE: {
    ...wallFaceRuleNew({
      id: "DEFAULT_WALL_CORE_RULE",
      name: "Set Default Wall Core",
      sortIndex: 0,
      filterId: "CONDITIONED_UNCONDITIONED_EXTERIOR_ROOM_ENCLOSURES" satisfies DefaultWallPlaneFilterId,
      direction: "POSITIVE_NORMAL",
      operation: defaultWallCoreOperation,
    }),
  },
  /**
   * 2. Next, we select all the same `solid` walls and apply exterior cladding to the OUTSIDE
   * only of each. In the even that two rooms are adjacent, the wall where they meet will end up
   * with exterior cladding on both sides. This is OK, since the next two rules will apply interior cladding
   * to the inside of each room.
   */
  DEFAULT_WALL_EXTERIOR_FINISH_OUTWARD_RULE: {
    ...wallFaceRuleNew({
      id: "DEFAULT_WALL_EXTERIOR_FINISH_OUTWARD_RULE",
      name: "Set Default Exterior Finish",
      sortIndex: 1,
      filterId: "CONDITIONED_UNCONDITIONED_EXTERIOR_ROOM_ENCLOSURES" satisfies DefaultWallPlaneFilterId,
      direction: "POSITIVE_NORMAL",
      operation: defaultExteriorFinishOperation,
    }),
  },
  /**
   * 3. Select all the walls that are deemed `interior` to the house, and apply the interior cladding
   * to the INSIDE of each. This will override the exterior cladding that may have been applied to the same
   * wall in the previous rule.
   */
  DEFAULT_WALL_INTERIOR_FINISH_INWARD_RULE: {
    ...wallFaceRuleNew({
      id: "DEFAULT_WALL_INTERIOR_FINISH_INWARD_RULE",
      name: "Set Default Interior Finish",
      sortIndex: 3,
      filterId: "CONDITIONED_UNCONDITIONED_ROOM_ENCLOSURES" satisfies DefaultWallPlaneFilterId,
      direction: "NEGATIVE_NORMAL",
      operation: defaultInteriorFinishOperation,
    }),
  },
  /**
   * 4. Select all the walls that are deemed `exterior` to the house, and apply the exterior cladding
   * to the OUTSIDE of each.
   */
  DEFAULT_WALL_EXTERIOR_FINISH_INWARD_RULE: {
    ...wallFaceRuleNew({
      id: "DEFAULT_WALL_EXTERIOR_FINISH_INWARD_RULE",
      name: "Set Default Exterior Finish",
      sortIndex: 4,
      filterId: "EXTERIOR_ROOM_ENCLOSURES" satisfies DefaultWallPlaneFilterId,
      direction: "NEGATIVE_NORMAL",
      operation: defaultExteriorFinishOperation,
    }),
  },
} satisfies Table<WallPlaneRuleEntity>;

function mergeWallPlaneRuleOperation(
  defaultOperation: WallPlaneOperation | undefined,
  docOperation: WallPlaneOperation | undefined,
): WallPlaneOperation | undefined {
  if (docOperation === undefined) {
    return defaultOperation;
  }
  if (defaultOperation === undefined) {
    return docOperation;
  }
  return {
    ...defaultOperation,
    ...docOperation,
  };
}

/**
 * TODO WALLS V2: Remove once all built-in wall plane rules are first-class Doc entities.
 * Merges default and doc override fields until then, including deep-merge of operation subfields
 * and fallback for built-ins that are not yet stored in doc.wallPlaneRules.
 */
export function resolveMergedWallPlaneRule(
  wallPlaneRules: Table<WallPlaneRuleEntity>,
  ruleId: string,
): WallPlaneRuleEntity | undefined {
  const defaultRule = ruleId in defaultWallPlaneRules ? defaultWallPlaneRules[ruleId] : undefined;
  const docRule = wallPlaneRules[ruleId];
  if (!defaultRule && !docRule) {
    return undefined;
  }
  if (!docRule) {
    return defaultRule;
  }
  if (!defaultRule) {
    return docRule;
  }

  return {
    ...defaultRule,
    ...docRule,
    operation: mergeWallPlaneRuleOperation(defaultRule.operation, docRule.operation),
  };
}

/**
 * TODO WALLS V2: Remove once all built-in wall plane rules are first-class Doc entities.
 * Materializes a full built-in rule copy into doc.wallPlaneRules on first override so stored
 * entities retain id and other default fields; later updates may remain partial patches.
 */
export function mergePropsForWallPlaneRuleDocUpdate(
  wallPlaneRules: Table<WallPlaneRuleEntity>,
  ruleId: string,
  partialProps: Partial<WallPlaneRuleEntity>,
): Partial<WallPlaneRuleEntity> {
  const mergedRule = resolveMergedWallPlaneRule(wallPlaneRules, ruleId);
  if (!mergedRule) {
    return partialProps;
  }

  const nextOperation =
    partialProps.operation !== undefined
      ? mergeWallPlaneRuleOperation(mergedRule.operation, partialProps.operation)
      : mergedRule.operation;

  const isFirstBuiltInOverride = ruleId in defaultWallPlaneRules && wallPlaneRules[ruleId] === undefined;
  if (isFirstBuiltInOverride) {
    return {
      ...mergedRule,
      ...partialProps,
      id: ruleId,
      operation: nextOperation,
    };
  }

  if (partialProps.operation !== undefined) {
    return {
      ...partialProps,
      operation: nextOperation,
    };
  }

  return partialProps;
}

/** Default built-in rules that do not yet have a doc override row. */
export function defaultWallPlaneRulesExcludingDocOverrides(
  wallPlaneRules: Table<WallPlaneRuleEntity>,
): readonly WallPlaneRuleEntity[] {
  const docRuleIds = new Set(Object.keys(wallPlaneRules));
  return Object.values(defaultWallPlaneRules).filter((rule) => !docRuleIds.has(rule.id));
}
